import React, { useState } from 'react';
import validator from 'email-validator';
import { useTranslation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import {
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  Button,
} from '@cargomatic/ui';
import { CircleAlert, LoaderCircle } from 'lucide-react';
import Alert from 'components/ui/Alert';

const SendRateCardEmailModal = ({
  show,
  onHide,
  onSend,
  defaultRecipient = '',
  modalTitle = 'EMAIL_RATE_CARD',
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { emails: defaultRecipient },
  });
  const title = t(modalTitle);
  const emailsLabel = titleize(t('RECIPIENT', { count: 2 }));
  const actionLabel = t('SEND');
  const cancelLabel = t('CANCEL');

  function validateEmails(value) {
    const emailRegex = /\S+@\S+\.\S+/;
    const isValid = value
      .split(',')
      .map((text) => text.trim())
      .every((email) => email && emailRegex.test(email));
    return isValid || t('INVALID_EMAILS');
  }

  async function send(formFields) {
    const { emails } = formFields;
    const to = emails.replace(/' '/g, '').split(',');

    onSend(_.uniq(to));
    onHide();
  }

  function renderError(error) {
    if (!error) {
      return '';
    }
    return <Alert className="mb-4" title={error} type="error" />;
  }

  return (
    <Dialog open={show} onOpenChange={onHide}>
      <form onSubmit={handleSubmit(send)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {renderError(errors?.emails?.message)}
          <div className={classNames({ error: !!errors.emails })}>
            <Label className="mb-2">{emailsLabel} *</Label>
            <Textarea
              passEventOnChange
              error={!!errors?.emails?.message}
              placeholder={t('ENTER_EMAILS_SEPARATED_BY_COMMAS')}
              {...register('emails', {
                validate: validateEmails,
                required: t('FIELD_IS_REQUIRED', { field: emailsLabel }),
              })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onHide} disabled={isSaving}>
              {cancelLabel}
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit(send)}
              disabled={isSaving}
            >
              <span>{actionLabel}</span>
              {isSaving && (
                <LoaderCircle className="w-4 h-4 animate-spin ml-2" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </form>
    </Dialog>
  );
};

SendRateCardEmailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onSend: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  defaultRecipient: PropTypes.string,
};

SendRateCardEmailModal.defaultProps = {
  show: false,
  defaultRecipient: [],
};

export default withTranslation()(SendRateCardEmailModal);
